// client/src/pages/School_Admin/SchoolSetupandConfiguration/SchoolSettingsPage.js
import { useState, useEffect, useRef } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import {
  Card,
  CardContent,
  CardHeader,
  Chip,
  Box,
  Button,
  FormControl,
  InputLabel,
  Grid,
  MenuItem,
  Select,
  Snackbar,
  Alert,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
  Switch,
  FormControlLabel,
  Divider,
  CircularProgress
} from '@mui/material';
import { MyLocation as MyLocationIcon, GpsFixed as GpsFixedIcon } from '@mui/icons-material';

import {fetchJson} from '../../../utils/fetchJson';
// The geofence policy, shared byte-for-byte with the phone — see the header of
// that file for why the two must never drift apart.
import { fixIsUsableAsCentre, centreAccuracyLimit, typedCoordinate } from '../../../utils/geofence';
// FIX_WINDOW_MS, GOOD_FIX_METRES and MIN_SAMPLES are gone from here: they were
// the ingredients of a sampling loop this page kept its own copy of. The loop
// is shared now, and those numbers are its business rather than this screen's.
import { schoolCentreFix, geolocationAvailable } from '../../../utils/browserLocation';

const SchoolSettingsPage = () => {
  const { t } = useTranslation();
  const apiBase = ((process.env.REACT_APP_API_BASE_URL || '').trim() || '').replace(/\/$/, '');
  const [school, setSchool] = useState({});
  const [years, setYears] = useState([]);
  const [currentYear, setCurrentYear] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tabValue, setTabValue] = useState('general');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const showSnackbar = (message, severity = 'success') => setSnackbar({ open: true, message, severity });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState('');
  const [stripeConnectStatus, setStripeConnectStatus] = useState(null);
  const [stripeConnecting, setStripeConnecting] = useState(false);
  const [detectingLocation, setDetectingLocation] = useState(false);
  // Shown live while detecting. The number is the point: an admin who can see
  // "±4200 m" understands immediately why the fix was refused, and that they
  // should be standing on campus with a phone rather than at a desk.
  const [detectedAccuracy, setDetectedAccuracy] = useState(null);
  const autoSeededLocationRef = useRef(false);

  const formatDateDMY = (raw) => {
    try {
      if (!raw) return '';
      const s = String(raw);
      const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (m) return `${m[3]}-${m[2]}-${m[1]}`;
      const d = new Date(raw);
      if (!Number.isFinite(d.getTime())) return s;
      const dd = String(d.getDate()).padStart(2, '0');
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const yyyy = String(d.getFullYear());
      return `${dd}-${mm}-${yyyy}`;
    } catch (_) {
      return String(raw || '');
    }
  };

  // Cameroon Region → Division (Department) mapping
  const CAMEROON_REGIONS = [
    'Adamawa', 'Centre', 'East', 'Far North', 'Littoral', 'North', 'North West', 'South', 'South West', 'West'
  ];
  const CAMEROON_DIVISIONS = {
    Adamawa: ['Djérem', 'Faro-et-Déo', 'Mayo-Banyo', 'Mbéré', 'Vina'],
    Centre: ['Haute-Sanaga', 'Lekié', 'Méfou-et-Afamba', 'Méfou-et-Akono', 'Mbam-et-Inoubou', 'Mbam-et-Kim', 'Mfoundi', 'Nyong-et-Kéllé', 'Nyong-et-Mfoumou', 'Nyong-et-Soʻo'],
    East: ['Boumba-et-Ngoko', 'Haut-Nyong', 'Kadey', 'Lom-et-Djérem'],
    'Far North': ['Diamaré', 'Logone-et-Chari', 'Mayo-Danay', 'Mayo-Kani', 'Mayo-Sava', 'Mayo-Tsanaga'],
    Littoral: ['Moungo', 'Nkam', 'Sanaga-Maritime', 'Wouri'],
    North: ['Bénoué', 'Faro', 'Mayo-Louti', 'Mayo-Rey'],
    'North West': ['Boyo', 'Bui', 'Donga-Mantung', 'Menchum', 'Mezam', 'Momo', 'Ngo-Ketunjia (Ngoketunjia)'],
    South: ['Dja-et-Lobo', 'Mvila', 'Océan', 'Vallée-du-Ntem'],
    'South West': ['Fako', 'Koupé-Manengouba', 'Lebialem', 'Manyu', 'Meme', 'Ndian'],
    West: ['Bamboutos', 'Haut-Nkam', 'Hauts-Plateaux', 'Koung-Khi', 'Ménoua', 'Mifi', 'Ndé', 'Noun']
  };

  const sectionLabel = (section) => t(`schoolSettingsPage.sections.${section}`);

  const handleStripeConnect = async () => {
    setStripeConnecting(true);
    try {
      const resp = await fetchJson('/api/stripe-connect/onboard', { method: 'POST' });
      const url = resp?.data?.url || resp?.url;
      if (!url) throw new Error(t('schoolSettingsPage.integrations.stripe.errors.onboardingUrlNotReturned'));
      window.location.assign(url);
    } catch (e) {
      const msg = (e && e.data && (e.data.message || e.data.error)) || e.message || t('schoolSettingsPage.integrations.stripe.errors.startOnboardingFailed');
      showSnackbar(msg, 'error');
    } finally {
      setStripeConnecting(false);
    }
  };

  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search || '');
      const tabParam = (params.get('tab') || '').toLowerCase();
      if (tabParam === 'integrations') {
        setTabValue('integrations');
      }
    } catch (_) {}
    fetchSchoolSettings();
    fetchAcademicYears();
  }, []);

  useEffect(() => {
    if (loading) return;
    if (autoSeededLocationRef.current) return;
    if (!school || !school.id) return;

    const hasLocation = !!(school?.country || school?.regionalDelegation || school?.divisionalDelegation);
    if (hasLocation) {
      autoSeededLocationRef.current = true;
      return;
    }

    autoSeededLocationRef.current = true;
    detectAndPrefillLocation({ persist: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, school?.id]);

  useEffect(() => {
    if (tabValue !== 'integrations') return;
    (async () => {
      try {
        const resp = await fetchJson('/api/stripe-connect/status', { method: 'GET' });
        setStripeConnectStatus(resp?.data || resp || null);
      } catch (_) {
        setStripeConnectStatus(null);
      }
    })();
  }, [tabValue]);

  const fetchSchoolSettings = async () => {
    try {
      const response = await fetchJson('/api/school-setup-and-configuration/school-settings', { method: 'GET' });
      setSchool((response && (response.data?.school || response.school || response)) || {});
    } catch (error) {
      showSnackbar(t('schoolSettingsPage.errors.fetchFailed'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleLogoSelect = (e) => {
    const file = e.target.files && e.target.files[0];
    setLogoFile(file || null);
    if (file) {
      const url = URL.createObjectURL(file);
      setLogoPreview(url);
    } else {
      setLogoPreview('');
    }
  };

  const handleLogoUpload = async () => {
    if (!logoFile) return showSnackbar(t('schoolSettingsPage.general.logo.errors.selectFileFirst'), 'warning');
    try {
      const form = new FormData();
      form.append('logo', logoFile);
      const data = await fetchJson('/api/school-setup-and-configuration/logo', {
        method: 'POST',
        body: form
      });
      const url = data.logoUrl || '';
      setSchool((s) => ({ ...s, logoUrl: url }));
      window.dispatchEvent(new CustomEvent('school:branding:updated', { detail: { logoUrl: url } }));
      showSnackbar(t('schoolSettingsPage.general.logo.snack.uploaded'));
    } catch (e) {
      showSnackbar(t('schoolSettingsPage.general.logo.errors.uploadFailed'), 'error');
    }
  };

  const resolveLogoUrl = (raw) => {
    if (!raw) return '';
    const s = String(raw).trim();
    if (!s) return '';
    if (/^https?:\/\//i.test(s)) return s;
    if (!apiBase) return s;
    return `${apiBase}${s.startsWith('/') ? '' : '/'}${s}`;
  };

  const fetchAcademicYears = async () => {
    try {
      const list = await fetchJson('/api/global-academic-years', { method: 'GET' });
      setYears(Array.isArray(list?.data) ? list.data : (Array.isArray(list) ? list : []));
      const cur = await fetchJson('/api/school-academic-years/current', { method: 'GET' });
      // Backend returns: { currentGlobalAcademicYearId, currentYear: {...} }
      const currentData = cur?.data || cur || null;
      setCurrentYear(currentData);
    } catch (e) {
      // Non-blocking
      console.error('Failed to fetch academic years:', e);
    }
  };

  const setCurrentAcademicYear = async (globalAcademicYearId) => {
    try {
      const response = await fetchJson('/api/school-academic-years/current', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ globalAcademicYearId })
      });
      showSnackbar(t('schoolSettingsPage.academicSession.snack.updated'));
      // Update with the full response from backend
      const updated = response?.data || response || null;
      setCurrentYear(updated);
    } catch (e) {
      showSnackbar(t('schoolSettingsPage.academicSession.errors.updateFailed'), 'error');
      console.error('Failed to set academic year:', e);
    }
  };

  const detectAndPrefillLocation = async ({ persist = false } = {}) => {
    try {
      // Only auto-fill if fields are empty to avoid overwriting manual values
      if (school?.country || school?.regionalDelegation || school?.divisionalDelegation) return;
      const resp = await fetch('https://ipapi.co/json');
      if (!resp.ok) return;
      const d = await resp.json();
      const country = d?.country_name || '';
      let region = d?.region || d?.region_code || '';
      // Normalize region name for Cameroon (strip trailing ' Region')
      if ((country || '').toLowerCase() === 'cameroon') {
        region = (region || '').replace(/\s*region$/i, '').trim();
      }
      let division = d?.county || d?.district || '';
      if ((country || '').toLowerCase() === 'cameroon') {
        // Use static mapping to choose first division if region recognized; otherwise leave blank
        const matchRegion = CAMEROON_REGIONS.find(r => r.toLowerCase() === (region || '').toLowerCase());
        division = matchRegion ? (CAMEROON_DIVISIONS[matchRegion]?.[0] || '') : '';
      }

      const next = { ...(school || {}), country, regionalDelegation: region, divisionalDelegation: division };
      setSchool(next);

      if (persist && (country || region || division)) {
        try {
          await fetchJson('/api/school-setup-and-configuration/school-settings', {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ general: next })
          });
        } catch (_) {}
      }
    } catch (_) {
      // ignore
    }
  };

  const handleSave = async (section, data) => {
    setSaving(true);
    try {
      await fetchJson('/api/school-setup-and-configuration/school-settings', { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ [section]: data }) });
      showSnackbar(t('schoolSettingsPage.snack.sectionUpdated', { section: sectionLabel(section) }), 'success');
    } catch (error) {
      const msg =
        (error && error.data && (error.data.message || error.data.error))
        || (error && error.message)
        || t('schoolSettingsPage.errors.sectionUpdateFailed', { section: sectionLabel(section) });
      showSnackbar(msg, 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div>{t('schoolSettingsPage.loading')}</div>;

  /**
   * What to say about the provenance of the pin currently saved.
   *
   * Three states, and they are genuinely different. UNTRUSTWORTHY is a fact the
   * admin must act on. NETWORK is the same fact with a specific cause and a
   * specific remedy, and it deserves the stronger wording because the reported
   * accuracy on such a fix looks reassuring and is not a measurement. UNKNOWN is
   * only a gap in the record — the pin may be perfect — so it is an invitation,
   * not a warning, and it is shown to administrators alone.
   */
  const pinNotice = (() => {
    const geo = school.geolocation || {};
    if (geo.latitude == null || geo.longitude == null) return null;
    const radius = Number(geo.geofenceRadius) || 200;

    if (geo.source === 'network') {
      return {
        severity: 'warning',
        text: t('schoolSettingsPage.geolocation.provenance.network', {
          accuracy: geo.accuracy ?? '?',
          defaultValue: 'This location was estimated from the network rather than from GPS. That can be hundreds of metres out even at a reported ±{{accuracy}} m, and every teacher’s distance is measured from it. Set it again from the phone app, outdoors on the campus.',
        }),
      };
    }
    if (geo.accuracy == null) {
      return {
        severity: 'info',
        text: t('schoolSettingsPage.geolocation.provenance.unknown', {
          defaultValue: 'This location was saved before the app recorded how accurate a reading was, so there is no way to tell how precise it is. Set it again from the phone app to confirm it.',
        }),
      };
    }
    if (!fixIsUsableAsCentre(geo.accuracy, radius)) {
      return {
        severity: 'warning',
        text: t('schoolSettingsPage.geolocation.provenance.tooRough', {
          accuracy: geo.accuracy,
          limit: Math.round(centreAccuracyLimit(radius)),
          defaultValue: 'This location was recorded from a ±{{accuracy}} m reading, and a school’s point has to be within ±{{limit}} m. Every teacher’s distance is measured from it, so they may be told they are far from the school while standing on it. Set it again from the phone app, outdoors on the campus.',
        }),
      };
    }
    return null;
  })();

  return (
    <div className="p-4 sm:p-6">
      <Typography variant="h5" fontWeight={700} mb={2}>{t('schoolSettingsPage.title')}</Typography>

      <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} sx={{ mb: 2 }}>
        <Tab label={t('schoolSettingsPage.tabs.general')} value="general" />
        <Tab label={t('schoolSettingsPage.tabs.integrations')} value="integrations" />
      </Tabs>

      {tabValue === 'general' && (
        <>
        <Card sx={{ mb: 2 }}>
          <CardHeader title={t('schoolSettingsPage.general.title')} />
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label={t('schoolSettingsPage.general.fields.schoolName')} value={school.name || ''} onChange={(e) => setSchool({ ...school, name: e.target.value })} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label={t('schoolSettingsPage.general.fields.schoolCode')} value={school.code || ''} InputProps={{ readOnly: true }} helperText={t('schoolSettingsPage.general.fields.schoolCodeHelper')} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('schoolSettingsPage.general.fields.currentAcademicYear') || t('schoolSettingsPage.academicSession.currentLabel') || 'Current Academic Year'}
                  value={
                    currentYear?.currentYear
                      ? `${currentYear.currentYear.name || currentYear.currentYear.yearName || ''}${currentYear.currentYear.startsAt && currentYear.currentYear.endsAt ? ` (${formatDateDMY(currentYear.currentYear.startsAt)} - ${formatDateDMY(currentYear.currentYear.endsAt)})` : ''}`
                      : (t('schoolSettingsPage.academicSession.notSet') || 'Not set')
                  }
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label={t('schoolSettingsPage.general.fields.address')} multiline minRows={3} value={school.address || ''} onChange={(e) => setSchool({ ...school, address: e.target.value })} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label={t('schoolSettingsPage.general.fields.websiteUrl')} value={school.websiteUrl || ''} onChange={(e) => setSchool({ ...school, websiteUrl: e.target.value })} />
              </Grid>
              <Grid item xs={12} md={6}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Button variant="outlined" component="label">
                    {t('schoolSettingsPage.general.logo.choose')}
                    <input hidden type="file" accept="image/*" onChange={handleLogoSelect} />
                  </Button>
                  <Button variant="contained" onClick={handleLogoUpload} disabled={!logoFile}>{t('schoolSettingsPage.general.logo.upload')}</Button>
                </Stack>
                <Box mt={2}>
                  {(logoPreview || school.logoUrl) && (
                    <img
                      src={logoPreview || resolveLogoUrl(school.logoUrl)}
                      alt={t('schoolSettingsPage.general.logo.previewAlt')}
                      style={{ maxHeight: 80, borderRadius: 4 }}
                    />
                  )}
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label={t('schoolSettingsPage.general.fields.primaryContact')} value={school.primaryContact || ''} onChange={(e) => setSchool({ ...school, primaryContact: e.target.value })} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label={t('schoolSettingsPage.general.fields.secondContact')} value={school.primaryContact2 || ''} onChange={(e) => setSchool({ ...school, primaryContact2: e.target.value })} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label={t('schoolSettingsPage.general.fields.officialEmail')} value={school.officialEmail || ''} onChange={(e) => setSchool({ ...school, officialEmail: e.target.value })} />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel id="default-language-label">{t('schoolSettingsPage.general.fields.defaultLanguage')}</InputLabel>
                  <Select labelId="default-language-label" label={t('schoolSettingsPage.general.fields.defaultLanguage')} value={school.defaultLanguage || ''} onChange={(e) => setSchool({ ...school, defaultLanguage: e.target.value })}>
                    <MenuItem value="en">{t('schoolSettingsPage.general.languages.en')}</MenuItem>
                    <MenuItem value="fr">{t('schoolSettingsPage.general.languages.fr')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth type="number" label={t('schoolSettingsPage.general.fields.establishmentYear')} value={school.establishmentYear || ''} onChange={(e) => setSchool({ ...school, establishmentYear: e.target.value })} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label={t('schoolSettingsPage.general.fields.motto')} value={school.motto || ''} onChange={(e) => setSchool({ ...school, motto: e.target.value })} />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel id="reg-status-label">{t('schoolSettingsPage.general.fields.registrationStatus')}</InputLabel>
                  <Select labelId="reg-status-label" label={t('schoolSettingsPage.general.fields.registrationStatus')} value={school.registrationStatus || ''} onChange={(e) => setSchool({ ...school, registrationStatus: e.target.value })}>
                    <MenuItem value="pending">{t('schoolSettingsPage.general.registrationStatuses.pending')}</MenuItem>
                    <MenuItem value="approved">{t('schoolSettingsPage.general.registrationStatuses.approved')}</MenuItem>
                    <MenuItem value="rejected">{t('schoolSettingsPage.general.registrationStatuses.rejected')}</MenuItem>
                    <MenuItem value="direct">{t('schoolSettingsPage.general.registrationStatuses.direct')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label={t('schoolSettingsPage.general.fields.country')} value={school.country || ''} onChange={(e) => setSchool({ ...school, country: e.target.value })} />
              </Grid>
              {/* Region (no 'Delegation' wording) */}
              <Grid item xs={12} md={6}>
                {String(school.country || '').toLowerCase() === 'cameroon' ? (
                  <Select
                    fullWidth
                    displayEmpty
                    value={school.regionalDelegation || ''}
                    onChange={(e) => {
                      const newRegion = e.target.value;
                      setSchool((s) => ({ ...s, regionalDelegation: newRegion, divisionalDelegation: '' }));
                    }}
                    renderValue={(val) => val || t('schoolSettingsPage.general.selectRegion')}
                  >
                    {CAMEROON_REGIONS.map((r) => (
                      <MenuItem key={r} value={r}>{r}</MenuItem>
                    ))}
                  </Select>
                ) : (
                  <TextField fullWidth label={t('schoolSettingsPage.general.fields.region')} value={school.regionalDelegation || ''} onChange={(e) => setSchool({ ...school, regionalDelegation: e.target.value })} />
                )}
              </Grid>
              {/* Division (Department) dependent on region; no 'Delegation' wording */}
              <Grid item xs={12} md={6}>
                {String(school.country || '').toLowerCase() === 'cameroon' ? (
                  <Select
                    fullWidth
                    displayEmpty
                    value={school.divisionalDelegation || ''}
                    onChange={(e) => setSchool((s) => ({ ...s, divisionalDelegation: e.target.value }))}
                    renderValue={(val) => val || t('schoolSettingsPage.general.selectDivision')}
                  >
                    {(CAMEROON_DIVISIONS[school.regionalDelegation] || []).map((d) => (
                      <MenuItem key={d} value={d}>{d}</MenuItem>
                    ))}
                  </Select>
                ) : (
                  <TextField fullWidth label={t('schoolSettingsPage.general.fields.division')} value={school.divisionalDelegation || ''} onChange={(e) => setSchool({ ...school, divisionalDelegation: e.target.value })} />
                )}
              </Grid>
            </Grid>

            <Box mt={3}>
              <Typography variant="subtitle1" gutterBottom>{t('schoolSettingsPage.academicSession.title')}</Typography>
              <Stack direction="row" spacing={2} alignItems="center">
                <Chip
                  color="primary"
                  label={t('schoolSettingsPage.academicSession.current', {
                    name: currentYear?.currentYear?.name || currentYear?.currentYear?.yearName || t('schoolSettingsPage.academicSession.notSet')
                  })}
                />
                <FormControl sx={{ minWidth: 280 }}>
                  <InputLabel id="year-select-label">{t('schoolSettingsPage.academicSession.selectAcademicYear')}</InputLabel>
                  <Select
                    labelId="year-select-label"
                    label={t('schoolSettingsPage.academicSession.selectAcademicYear')}
                    value={currentYear?.currentGlobalAcademicYearId || ''}
                    onChange={(e) => setCurrentAcademicYear(e.target.value)}
                    disabled={!years || years.length === 0}
                  >
                    {(years || []).length === 0 && (
                      <MenuItem disabled>{t('schoolSettingsPage.academicSession.noAcademicYearsAvailable')}</MenuItem>
                    )}
                    {(years || []).map((y) => (
                      <MenuItem key={y.id} value={y.id}>
                        {y.name || y.yearName || (y.startYear && y.endYear ? `${y.startYear}-${y.endYear}` : t('schoolSettingsPage.academicSession.yearWithId', { id: y.id }))}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
              {years && years.length === 0 && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  {t('schoolSettingsPage.academicSession.noAcademicYearsInfo')}
                </Alert>
              )}
            </Box>

            <Box mt={2}>
              <Button 
                variant="contained"
                onClick={() => handleSave('general', { 
                  name: school.name,
                  address: school.address,
                  logoUrl: school.logoUrl,
                  websiteUrl: school.websiteUrl,
                  appWebsiteUrl: school.appWebsiteUrl,
                  primaryContact: school.primaryContact,
                  primaryContact2: school.primaryContact2,
                  officialEmail: school.officialEmail,
                  defaultLanguage: school.defaultLanguage,
                  establishmentYear: school.establishmentYear,
                  motto: school.motto,
                  registrationStatus: school.registrationStatus,
                  country: school.country,
                  regionalDelegation: school.regionalDelegation,
                  divisionalDelegation: school.divisionalDelegation,
                  ministry: undefined
                })}
                disabled={saving}
              >
                {t('schoolSettingsPage.general.saveButton')}
              </Button>
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>{t('schoolSettingsPage.documents.title') || 'Document Defaults'}</Typography>
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              {t('schoolSettingsPage.documents.subtitle') || 'Default letterhead and language for official documents (class lists, result sheets; also the default for report cards/transcripts, which can be overridden per generation).'}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>{t('schoolSettingsPage.documents.letterhead') || 'Default letterhead'}</InputLabel>
                  <Select
                    label={t('schoolSettingsPage.documents.letterhead') || 'Default letterhead'}
                    value={school.reportCardLetterhead || 'default'}
                    onChange={(e) => setSchool({ ...school, reportCardLetterhead: e.target.value })}
                  >
                    <MenuItem value="default">{t('schoolSettingsPage.documents.lhDefault') || 'Default (bilingual ministry)'}</MenuItem>
                    <MenuItem value="ministry_school_en">{t('schoolSettingsPage.documents.lhEn') || 'Ministry + School info (English)'}</MenuItem>
                    <MenuItem value="ministry_school_fr">{t('schoolSettingsPage.documents.lhFr') || 'Ministry + School info (French)'}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>{t('schoolSettingsPage.documents.language') || 'Default report language'}</InputLabel>
                  <Select
                    label={t('schoolSettingsPage.documents.language') || 'Default report language'}
                    value={school.reportCardLanguage || 'en'}
                    onChange={(e) => setSchool({ ...school, reportCardLanguage: e.target.value })}
                  >
                    <MenuItem value="en">{t('schoolSettingsPage.documents.en') || 'English'}</MenuItem>
                    <MenuItem value="fr">{t('schoolSettingsPage.documents.fr') || 'French'}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            <Box mt={2}>
              <Button
                variant="contained"
                disabled={saving}
                onClick={() => handleSave('year', { reportCardLetterhead: school.reportCardLetterhead || 'default', reportCardLanguage: school.reportCardLanguage || 'en' })}
              >
                {t('schoolSettingsPage.documents.saveButton') || 'Save'}
              </Button>
            </Box>
          </CardContent>
        </Card>
        </>
      )}

      {tabValue === 'integrations' && (
        <Card sx={{ mb: 2 }}>
          <CardHeader
            title={<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><GpsFixedIcon color="primary" /> {t('schoolSettingsPage.geolocation.title')}</Box>}
            subheader={t('schoolSettingsPage.geolocation.subheader')}
          />
          <CardContent>
            <FormControlLabel
              control={
                <Switch
                  checked={!!(school.geolocation?.enforceGeofence)}
                  onChange={(e) => setSchool(s => ({ ...s, geolocation: { ...(s.geolocation || {}), enforceGeofence: e.target.checked } }))}
                />
              }
              label={<Typography variant="body1" fontWeight={600}>{t('schoolSettingsPage.geolocation.enforceLabel')}</Typography>}
            />
            {/* SAYS WHAT BOTH POSITIONS MEAN, not just the "on" one. Leaving
                this off is a supported way to run the school rather than a
                setting nobody got round to, and a school whose GPS is
                unreliable is entitled to say so. Same wording as the phone. */}
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2, ml: 6 }}>
              {school.geolocation?.enforceGeofence
                ? t('schoolSettingsPage.geolocation.enforceHelpOn', {
                  defaultValue: 'On — a register can only be filed inside the radius below. Teachers must allow location access.',
                })
                : t('schoolSettingsPage.geolocation.enforceHelpOff', {
                  defaultValue: 'Off — teachers take attendance from anywhere, and the app never asks for their location. Leave it off if GPS is unreliable at your school.',
                })}
            </Typography>

            <Divider sx={{ my: 2 }} />

            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label={t('schoolSettingsPage.geolocation.fields.latitude')}
                  type="number"
                  value={school.geolocation?.latitude ?? ''}
                  onChange={(e) => setSchool(s => ({ ...s, geolocation: typedCoordinate(s.geolocation, { latitude: e.target.value }) }))}
                  placeholder={t('schoolSettingsPage.geolocation.fields.latitudePlaceholder')}
                  inputProps={{ step: 'any' }}
                  helperText={t('schoolSettingsPage.geolocation.fields.latitudeHelper')}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label={t('schoolSettingsPage.geolocation.fields.longitude')}
                  type="number"
                  value={school.geolocation?.longitude ?? ''}
                  onChange={(e) => setSchool(s => ({ ...s, geolocation: typedCoordinate(s.geolocation, { longitude: e.target.value }) }))}
                  placeholder={t('schoolSettingsPage.geolocation.fields.longitudePlaceholder')}
                  inputProps={{ step: 'any' }}
                  helperText={t('schoolSettingsPage.geolocation.fields.longitudeHelper')}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label={t('schoolSettingsPage.geolocation.fields.radius')}
                  type="number"
                  value={school.geolocation?.geofenceRadius ?? 200}
                  onChange={(e) => setSchool(s => ({ ...s, geolocation: { ...(s.geolocation || {}), geofenceRadius: e.target.value } }))}
                  inputProps={{ min: 50, max: 5000 }}
                  helperText={t('schoolSettingsPage.geolocation.fields.radiusHelper')}
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="outlined"
                  startIcon={detectingLocation ? <CircularProgress size={18} /> : <MyLocationIcon />}
                  disabled={detectingLocation}
                  onClick={async () => {
                    if (!geolocationAvailable()) {
                      showSnackbar(t('schoolSettingsPage.geolocation.errors.notSupported'), 'error');
                      return;
                    }
                    setDetectingLocation(true);
                    setDetectedAccuracy(null);

                    // This coordinate is the anti-fraud control for attendance:
                    // the register records that the TEACHER was present, and the
                    // geofence is the only thing separating a real record from
                    // one filed at home. A poor fix does not merely reduce
                    // precision, it destroys the control — it either blocks
                    // everyone on site or admits everyone off it.
                    //
                    // THE SAMPLING LOOP USED TO BE INLINE HERE, about a hundred
                    // lines of it, hand-matched to the phone's numbers. It is
                    // `schoolCentreFix` now: the same routine the register runs,
                    // through the same shared reducer. Matching by hand is what
                    // let the OTHER web screen drift to an 8-second window with
                    // no sample floor without anyone noticing.
                    const radius = Number(school.geolocation?.geofenceRadius) || 200;
                    try {
                      const { fix, usable, source } = await schoolCentreFix(
                        radius,
                        (partial) => setDetectedAccuracy(Math.round(partial.accuracy || 0)),
                      );
                      const acc = Math.round(fix.accuracy || 0);
                      setDetectedAccuracy(acc);

                      // Refusing is safe: the latitude and longitude fields above
                      // are editable, so an admin can always type coordinates
                      // read off a map. Silently accepting a fix that cannot
                      // distinguish "on campus" from "in the next district" is
                      // the outcome with no recovery.
                      //
                      // A NETWORK ESTIMATE IS REFUSED WHATEVER IT CLAIMS, and on
                      // a desktop that is most of them: there is no GNSS in the
                      // machine, so the browser answers from a Wi-Fi database
                      // with a confident-looking ±20m that can be kilometres
                      // out. No accuracy bar can catch that, because the number
                      // is not a measurement — only the reading's BEHAVIOUR
                      // gives it away, and that is what `source` reports.
                      if (source === 'network') {
                        showSnackbar(t('schoolSettingsPage.geolocation.errors.networkFix', {
                          accuracy: acc,
                          defaultValue: 'This device estimated its position from the network rather than from GPS. That can be hundreds of metres out even when it reports a small margin of ±{{accuracy}} m. Set the school location from the phone app, outdoors on the campus.',
                        }), 'error');
                        return;
                      }

                      // THE GATE IS THE CENTRE GATE, not `acc > radius`. That old
                      // rule is not a rule: it lets the centre of a 200m fence be
                      // 200m wrong, which can put the whole fence off the campus,
                      // and it is why an admin could store a network-derived point
                      // that then measured a teacher standing on the grounds as a
                      // kilometre away. A teacher's bad fix is one bad register; a
                      // bad centre moves the fence for everybody until someone
                      // notices. Shared with the phone via geofence.js.
                      if (!usable) {
                        showSnackbar(t('schoolSettingsPage.geolocation.errors.accuracyTooLow', {
                          accuracy: acc, radius: Math.round(centreAccuracyLimit(radius)),
                        }), 'error');
                        return;
                      }

                      setSchool(s => ({
                        ...s,
                        geolocation: {
                          ...(s.geolocation || {}),
                          latitude: parseFloat(fix.latitude.toFixed(6)),
                          longitude: parseFloat(fix.longitude.toFixed(6)),
                          // Persisted so a surveyed point stays distinguishable
                          // from a guess after the fact. Nothing read these
                          // before, which is why a bad coordinate was invisible.
                          accuracy: acc,
                          capturedAt: new Date().toISOString(),
                          source,
                        }
                      }));
                      showSnackbar(t('schoolSettingsPage.geolocation.snack.locationDetectedAccurate', { accuracy: acc }));
                    } catch (err) {
                      showSnackbar(t('schoolSettingsPage.geolocation.errors.detectionFailed', {
                        message: err?.message || t('schoolSettingsPage.geolocation.errors.noFix'),
                      }), 'error');
                    } finally {
                      setDetectingLocation(false);
                    }
                  }}
                >
                  {detectingLocation ? t('schoolSettingsPage.geolocation.buttons.detecting') : t('schoolSettingsPage.geolocation.buttons.detect')}
                </Button>
                <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                  {t('schoolSettingsPage.geolocation.detectHintAccurate')}
                </Typography>
                {detectedAccuracy != null && (
                  <Typography
                    variant="caption"
                    sx={{ ml: 2 }}
                    // THE SAME GATE THE BUTTON ABOVE APPLIES. This was still
                    // painting by the discarded `accuracy > radius` rule, so on
                    // a 200m fence a ±150m reading showed green and was then
                    // refused sixty lines later — the caption said the fix was
                    // fine and the app disagreed. The phone has always used the
                    // shared gate here.
                    color={fixIsUsableAsCentre(detectedAccuracy, Number(school.geolocation?.geofenceRadius) || 200)
                      ? 'success.main' : 'error.main'}
                  >
                    {t('schoolSettingsPage.geolocation.accuracyReading', { accuracy: detectedAccuracy })}
                  </Typography>
                )}
              </Grid>
            </Grid>

            {school.geolocation?.latitude && school.geolocation?.longitude && (
              <Alert severity="success" sx={{ mt: 2 }}>
                <Trans
                  i18nKey="schoolSettingsPage.geolocation.alerts.coordinatesSet"
                  values={{
                    lat: String(school.geolocation.latitude),
                    lng: String(school.geolocation.longitude),
                    radius: school.geolocation.geofenceRadius || 200
                  }}
                  components={{ strong: <strong /> }}
                />
                {school.geolocation.enforceGeofence ? ` ${t('schoolSettingsPage.geolocation.alerts.geofencingActive')}` : ` ${t('schoolSettingsPage.geolocation.alerts.geofencingDisabled')}`}
              </Alert>
            )}

            {!school.geolocation?.latitude && school.geolocation?.enforceGeofence && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                {t('schoolSettingsPage.geolocation.alerts.enabledNoCoordinates')}
              </Alert>
            )}

            {/* HOW THIS PIN WAS TAKEN. The two fields have always been stored
                and never shown, so a coordinate surveyed on campus to ±5m looked
                exactly like a Wi-Fi guess several kilometres out — and since
                every teacher's distance is measured from this point, that
                difference decides whether the control works at all. */}
            {pinNotice && (
              <Alert severity={pinNotice.severity} sx={{ mt: 2 }}>
                {pinNotice.text}
              </Alert>
            )}

            <Box mt={2}>
              <Button
                variant="contained"
                onClick={() => handleSave('geolocation', school.geolocation || {})}
                disabled={saving}
              >
                {t('schoolSettingsPage.geolocation.saveButton')}
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {tabValue === 'integrations' && (
        <Card sx={{ mb: 2 }}>
          <CardHeader
            title={t('schoolSettingsPage.paymentAccounts.title', 'Payment Account Details')}
            subheader={t('schoolSettingsPage.paymentAccounts.description', 'Bank and mobile money accounts parents should pay into. These are shown to parents when they submit a payment receipt for verification.')}
          />
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" fontWeight={600}>{t('schoolSettingsPage.paymentAccounts.bank', 'Bank Transfer')}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label={t('schoolSettingsPage.paymentAccounts.bankName', 'Bank Name')} value={school.bankName || ''} onChange={(e) => setSchool({ ...school, bankName: e.target.value })} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label={t('schoolSettingsPage.paymentAccounts.accountName', 'Account Name')} value={school.bankAccountName || ''} onChange={(e) => setSchool({ ...school, bankAccountName: e.target.value })} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label={t('schoolSettingsPage.paymentAccounts.accountNumber', 'Account Number')} value={school.bankAccountNumber || ''} onChange={(e) => setSchool({ ...school, bankAccountNumber: e.target.value })} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label={t('schoolSettingsPage.paymentAccounts.branch', 'Branch')} value={school.bankBranch || ''} onChange={(e) => setSchool({ ...school, bankBranch: e.target.value })} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label={t('schoolSettingsPage.paymentAccounts.iban', 'IBAN')} value={school.bankIban || ''} onChange={(e) => setSchool({ ...school, bankIban: e.target.value })} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label={t('schoolSettingsPage.paymentAccounts.swift', 'SWIFT / BIC')} value={school.bankSwift || ''} onChange={(e) => setSchool({ ...school, bankSwift: e.target.value })} />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2" fontWeight={600}>{t('schoolSettingsPage.paymentAccounts.mobileMoney', 'Mobile Money')}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label={t('schoolSettingsPage.paymentAccounts.mtnNumber', 'MTN MoMo Number')} value={school.mtnAccountNumber || ''} onChange={(e) => setSchool({ ...school, mtnAccountNumber: e.target.value })} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label={t('schoolSettingsPage.paymentAccounts.orangeNumber', 'Orange Money Number')} value={school.orangeAccountNumber || ''} onChange={(e) => setSchool({ ...school, orangeAccountNumber: e.target.value })} />
              </Grid>
            </Grid>
            <Box mt={2}>
              <Button
                variant="contained"
                onClick={() => handleSave('integrations', {
                  bankName: school.bankName,
                  bankAccountName: school.bankAccountName,
                  bankAccountNumber: school.bankAccountNumber,
                  bankBranch: school.bankBranch,
                  bankIban: school.bankIban,
                  bankSwift: school.bankSwift,
                  mtnAccountNumber: school.mtnAccountNumber,
                  orangeAccountNumber: school.orangeAccountNumber,
                })}
                disabled={saving}
              >
                {t('schoolSettingsPage.paymentAccounts.saveButton', 'Save Payment Details')}
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {tabValue === 'integrations' && (
        <Card sx={{ mb: 2 }}>
          <CardHeader title={t('schoolSettingsPage.integrations.title')} />
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mt: 1, display: 'flex', alignItems: 'center' }}>
                  <Box component="span" sx={{ bgcolor: '#635bff', color: '#fff', fontWeight: 700, borderRadius: 1, px: 1, py: 0.3, mr: 1, fontSize: 13 }}>S</Box>
                  {t('schoolSettingsPage.integrations.stripe.title')}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('schoolSettingsPage.integrations.stripe.destinationAccountId')}
                  value={school.stripeDestinationAccountId || ''}
                  onChange={(e) => setSchool({ ...school, stripeDestinationAccountId: e.target.value })}
                  disabled={!!stripeConnectStatus?.connected}
                  helperText={
                    stripeConnectStatus?.connected
                      ? t('schoolSettingsPage.integrations.stripe.status.connectedHelp', 'Connected via Stripe Connect. Account ID is managed automatically.')
                      : t('schoolSettingsPage.integrations.stripe.status.notConnectedHelp', 'Not connected yet. Click Connect Stripe to generate an account ID.')
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ height: '100%' }}>
                  <Button variant="outlined" onClick={handleStripeConnect} disabled={stripeConnecting}>
                    {t('schoolSettingsPage.integrations.stripe.connectButton')}
                  </Button>
                  <Typography variant="body2" color="text.secondary">
                    {stripeConnectStatus?.connected
                      ? t('schoolSettingsPage.integrations.stripe.status.connected', {
                        status: stripeConnectStatus?.charges_enabled
                          ? t('schoolSettingsPage.integrations.stripe.status.chargesEnabled')
                          : t('schoolSettingsPage.integrations.stripe.status.chargesPending')
                      })
                      : t('schoolSettingsPage.integrations.stripe.status.notConnected')}
                  </Typography>
                </Stack>
              </Grid>
              {stripeConnectStatus?.connected && (
                <>
                  <Grid item xs={12} md={4}>
                    <Chip
                      label={stripeConnectStatus?.charges_enabled ? t('schoolSettingsPage.integrations.stripe.status.chargesEnabled') : t('schoolSettingsPage.integrations.stripe.status.chargesPending')}
                      color={stripeConnectStatus?.charges_enabled ? 'success' : 'warning'}
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Chip
                      label={stripeConnectStatus?.payouts_enabled ? t('schoolSettingsPage.integrations.stripe.status.payoutsEnabled', 'Payouts enabled') : t('schoolSettingsPage.integrations.stripe.status.payoutsPending', 'Payouts pending')}
                      color={stripeConnectStatus?.payouts_enabled ? 'success' : 'warning'}
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Chip
                      label={stripeConnectStatus?.details_submitted ? t('schoolSettingsPage.integrations.stripe.status.detailsSubmitted', 'Details submitted') : t('schoolSettingsPage.integrations.stripe.status.detailsMissing', 'Details missing')}
                      color={stripeConnectStatus?.details_submitted ? 'success' : 'default'}
                      variant="outlined"
                    />
                  </Grid>
                </>
              )}
              <Grid item xs={12}>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mt: 1 }}>{t('schoolSettingsPage.integrations.mobileMoney')}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mt: 0.5, display: 'flex', alignItems: 'center' }}>
                  <Box component="span" sx={{ bgcolor: '#FFCC00', color: '#000', fontWeight: 700, borderRadius: 1, px: 1, py: 0.3, mr: 1, fontSize: 13 }}>MTN</Box>
                  {t('schoolSettingsPage.integrations.mtnMomo.title')}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth autoComplete="off" label={t('schoolSettingsPage.integrations.mtnMomo.fields.baseUrl')} value={school.mtnMomoBaseURL || ''} onChange={(e) => setSchool({ ...school, mtnMomoBaseURL: e.target.value })} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth autoComplete="off" label={t('schoolSettingsPage.integrations.mtnMomo.fields.targetEnvironment')} value={school.mtnMomoTargetEnvironment || ''} onChange={(e) => setSchool({ ...school, mtnMomoTargetEnvironment: e.target.value })} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth autoComplete="new-password" label={t('schoolSettingsPage.integrations.mtnMomo.fields.subscriptionKey')} type="password" value={school.mtnMomoSubscriptionKey || ''} onChange={(e) => setSchool({ ...school, mtnMomoSubscriptionKey: e.target.value })} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth autoComplete="off" label={t('schoolSettingsPage.integrations.mtnMomo.fields.apiUser')} value={school.mtnMomoApiUser || ''} onChange={(e) => setSchool({ ...school, mtnMomoApiUser: e.target.value })} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth autoComplete="new-password" label={t('schoolSettingsPage.integrations.mtnMomo.fields.apiKey')} type="password" value={school.mtnMomoApiKey || ''} onChange={(e) => setSchool({ ...school, mtnMomoApiKey: e.target.value })} />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mt: 1, display: 'flex', alignItems: 'center' }}>
                  <Box component="span" sx={{ bgcolor: '#ff6600', color: '#fff', fontWeight: 700, borderRadius: 1, px: 1, py: 0.3, mr: 1, fontSize: 13 }}>OM</Box>
                  {t('schoolSettingsPage.integrations.orangeMoney.title')}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth autoComplete="new-password" label={t('schoolSettingsPage.integrations.orangeMoney.fields.merchantCode')} type="password" value={school.orangeMerchantKey || school.orangeMerchantCode || ''} onChange={(e) => setSchool({ ...school, orangeMerchantKey: e.target.value })} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth autoComplete="new-password" label={t('schoolSettingsPage.integrations.orangeMoney.fields.authHeader')} type="password" value={school.orangeAuthHeader || ''} onChange={(e) => setSchool({ ...school, orangeAuthHeader: e.target.value })} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth autoComplete="off" label={t('schoolSettingsPage.integrations.orangeMoney.fields.country')} value={school.orangeCountry || ''} onChange={(e) => setSchool({ ...school, orangeCountry: e.target.value })} />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1" fontWeight={600} sx={{ mt: 1 }}>
                  {t('schoolSettingsPage.integrations.salaryPayout.title')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {t('schoolSettingsPage.integrations.salaryPayout.description')}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mt: 0.5, display: 'flex', alignItems: 'center' }}>
                  <Box component="span" sx={{ bgcolor: '#FFCC00', color: '#000', fontWeight: 700, borderRadius: 1, px: 1, py: 0.3, mr: 1, fontSize: 13 }}>MTN</Box>
                  {t('schoolSettingsPage.integrations.salaryPayout.mtn.title')}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  autoComplete="off"
                  label={t('schoolSettingsPage.integrations.salaryPayout.mtn.fields.baseUrl')}
                  value={school.mtnDisbursementBaseURL || ''}
                  onChange={(e) => setSchool({ ...school, mtnDisbursementBaseURL: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  autoComplete="off"
                  label={t('schoolSettingsPage.integrations.salaryPayout.mtn.fields.targetEnvironment')}
                  value={school.mtnDisbursementTargetEnvironment || ''}
                  onChange={(e) => setSchool({ ...school, mtnDisbursementTargetEnvironment: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  autoComplete="new-password"
                  type="password"
                  label={t('schoolSettingsPage.integrations.salaryPayout.mtn.fields.subscriptionKey')}
                  value={school.mtnDisbursementSubscriptionKey || ''}
                  onChange={(e) => setSchool({ ...school, mtnDisbursementSubscriptionKey: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  autoComplete="off"
                  label={t('schoolSettingsPage.integrations.salaryPayout.mtn.fields.apiUser')}
                  value={school.mtnDisbursementApiUser || ''}
                  onChange={(e) => setSchool({ ...school, mtnDisbursementApiUser: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  autoComplete="new-password"
                  type="password"
                  label={t('schoolSettingsPage.integrations.salaryPayout.mtn.fields.apiKey')}
                  value={school.mtnDisbursementApiKey || ''}
                  onChange={(e) => setSchool({ ...school, mtnDisbursementApiKey: e.target.value })}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mt: 1, display: 'flex', alignItems: 'center' }}>
                  <Box component="span" sx={{ bgcolor: '#ff6600', color: '#fff', fontWeight: 700, borderRadius: 1, px: 1, py: 0.3, mr: 1, fontSize: 13 }}>OM</Box>
                  {t('schoolSettingsPage.integrations.salaryPayout.orange.title')}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  autoComplete="off"
                  label={t('schoolSettingsPage.integrations.salaryPayout.orange.fields.baseUrl')}
                  value={school.orangePayoutBaseURL || ''}
                  onChange={(e) => setSchool({ ...school, orangePayoutBaseURL: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  autoComplete="off"
                  label={t('schoolSettingsPage.integrations.salaryPayout.orange.fields.country')}
                  value={school.orangePayoutCountry || ''}
                  onChange={(e) => setSchool({ ...school, orangePayoutCountry: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  autoComplete="new-password"
                  type="password"
                  label={t('schoolSettingsPage.integrations.salaryPayout.orange.fields.merchantKey')}
                  value={school.orangePayoutMerchantKey || ''}
                  onChange={(e) => setSchool({ ...school, orangePayoutMerchantKey: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  autoComplete="new-password"
                  type="password"
                  label={t('schoolSettingsPage.integrations.salaryPayout.orange.fields.authHeader')}
                  value={school.orangePayoutAuthHeader || ''}
                  onChange={(e) => setSchool({ ...school, orangePayoutAuthHeader: e.target.value })}
                />
              </Grid>
            </Grid>
            <Box mt={2}>
              <Button 
                variant="contained"
                onClick={() => handleSave('integrations', {
                  stripeDestinationAccountId: school.stripeDestinationAccountId,
                  mtnMomoBaseURL: school.mtnMomoBaseURL,
                  mtnMomoTargetEnvironment: school.mtnMomoTargetEnvironment,
                  mtnMomoSubscriptionKey: school.mtnMomoSubscriptionKey,
                  mtnMomoApiUser: school.mtnMomoApiUser,
                  mtnMomoApiKey: school.mtnMomoApiKey,
                  orangeMerchantKey: school.orangeMerchantKey || school.orangeMerchantCode,
                  orangeAuthHeader: school.orangeAuthHeader,
                  orangeCountry: school.orangeCountry,
                  mtnDisbursementBaseURL: school.mtnDisbursementBaseURL,
                  mtnDisbursementTargetEnvironment: school.mtnDisbursementTargetEnvironment,
                  mtnDisbursementSubscriptionKey: school.mtnDisbursementSubscriptionKey,
                  mtnDisbursementApiUser: school.mtnDisbursementApiUser,
                  mtnDisbursementApiKey: school.mtnDisbursementApiKey,
                  orangePayoutBaseURL: school.orangePayoutBaseURL,
                  orangePayoutCountry: school.orangePayoutCountry,
                  orangePayoutMerchantKey: school.orangePayoutMerchantKey,
                  orangePayoutAuthHeader: school.orangePayoutAuthHeader,
                })}
                disabled={saving}
              >
                {t('schoolSettingsPage.integrations.saveButton')}
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default SchoolSettingsPage;
